import React, { createContext, useState, useEffect, useContext } from 'react';
import { getPreference, setPreference } from '../utils/storage';

interface Preferences {
  [key: string]: unknown;
}

interface PreferencesContextValue {
  preferences: Preferences;
  setPreferences: React.Dispatch<React.SetStateAction<Preferences>>;
  resetQuestionnaire: boolean;
  setResetQuestionnaire: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

export const PreferencesProvider = ({ children }: { children: React.ReactNode }) => {
  const [preferences, setPreferences] = useState<Preferences>(() => getPreference('userPreferences') || {});
  const [resetQuestionnaire, setResetQuestionnaire] = useState(false);

  useEffect(() => {
    setPreference('userPreferences', preferences);
  }, [preferences]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'userPreferences') {
        try {
          const next = e.newValue ? JSON.parse(e.newValue) : {};
          setPreferences(next);
        } catch {
          setPreferences({});
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <PreferencesContext.Provider value={{ preferences, setPreferences, resetQuestionnaire, setResetQuestionnaire }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = (): PreferencesContextValue => {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};
